import { useAppState } from '@react-native-community/hooks';
import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useAsyncEffect from 'use-async-effect';

import { useAppConfig } from '~/contexts/app-config';
import { useIntl } from '~/contexts/intl';
import * as ErrorDiagnostics from '~/error';
import { LanguageCode } from '~/types';

import {
    createServiceNoticeClient,
    fetchServiceNotice,
    ServiceNotice,
    ServiceNoticeUnreachableError
} from './service-notice-client';
import { fallbackServiceEndHasPassed } from './service-notice-fallback';
import {
    clearPersistedNotice,
    clearServiceEndedByFallback,
    getNoticeDismissedAt,
    getPersistedNotice,
    getServiceEndedByFallback,
    setNoticeDismissedAt,
    setPersistedNotice,
    setServiceEndedByFallback
} from './service-notice-persist';

export type { ServiceNotice } from './service-notice-client';
export { isHttpUrl } from './service-notice-client';
export { FALLBACK_MORE_INFO_URL } from './service-notice-fallback';

/**
 * - 'unknown': persisted notice state is still being read on boot
 * - 'active': the service is available (with or without a notice to show)
 * - 'unavailable': the notice validity has passed and the service is no longer available
 */
export type ServiceNoticeState = 'unknown' | 'active' | 'unavailable';

export type ServiceNoticeContextType = {
    state: ServiceNoticeState;
    notice: ServiceNotice | null;
    noticeVisible: boolean;
    dismissNotice: () => Promise<void>;
};

/** How long a dismissed notice stays hidden before it is shown again (daily reminder) */
export const NOTICE_REMINDER_INTERVAL = 24 * 60 * 60 * 1000; // ms

/** Upper limit for one validity check timer step (also keeps the delay far from the setTimeout maximum) */
export const NOTICE_VALIDITY_CHECK_MAX_INTERVAL = 60_000; // ms

export const ServiceNoticeContext = createContext<ServiceNoticeContextType | undefined>(undefined);

/** True when the notice validity has passed, i.e. the service is no longer available */
function noticeValidityHasPassed(notice: ServiceNotice) {
    return Date.now() >= Date.parse(notice.validUntil);
}

/**
 * Tracks the service notice published by the server and how it affects the app:
 * whether the notice popup should be visible and whether the notice validity has
 * already passed (in which case the app renders only a static screen and no network
 * traffic is started).
 *
 * NOTE: this provider is mounted above auth/analytics on purpose, so it must not use
 * anything that requires those contexts.
 */
function ServiceNoticeContextProvider(props: PropsWithChildren<object>) {
    const { children } = props;
    const { config } = useAppConfig();
    const { locale } = useIntl();
    const appStatus = useAppState();

    const [state, setState] = useState<ServiceNoticeState>('unknown');
    const [notice, setNotice] = useState<ServiceNotice | null>(null);
    const [dismissedAt, setDismissedAt] = useState<Date | null>(null);

    // Boot: read the persisted notice state before anything network-capable is started.
    // When the persisted notice validity has already passed, the app stays fully
    // offline: no error diagnostics initialization and no notice fetching.
    useAsyncEffect(async () => {
        try {
            const persistedNotice = await getPersistedNotice();
            const persistedDismissedAt = await getNoticeDismissedAt();

            if (persistedNotice) {
                // Keep the persisted notice available (e.g. its moreInfoUrl) in both states
                setNotice(persistedNotice);

                if (noticeValidityHasPassed(persistedNotice)) {
                    setState('unavailable');
                    return;
                }
            } else if (await getServiceEndedByFallback()) {
                // No notice was ever received from the server, but the app has already found
                // the service closed by the fallback end date (see service-notice-fallback.ts).
                // Stay fully offline, exactly as with a persisted notice that has expired.
                setState('unavailable');
                return;
            }

            if (persistedDismissedAt) {
                setDismissedAt(persistedDismissedAt);
            }

            // Normal startup: error diagnostics are initialized here (instead of at module
            // import time) so that nothing is initialized when the notice validity has passed
            ErrorDiagnostics.initializeErrorDiagnostics();
            setState('active');
        } catch {
            // Fail OPEN on any error reading persisted storage. The storage helpers guard
            // JSON parsing but not the underlying read, so a rejected AsyncStorage.getItem
            // would otherwise leave the state stuck at 'unknown' forever (the gate renders
            // nothing while 'unknown') and blank the whole app. A storage read failure must
            // never blank or gate the app: start diagnostics and treat the service as
            // available, exactly as if there were no persisted notice.
            ErrorDiagnostics.initializeErrorDiagnostics();
            setState('active');
        }
    }, []);

    // Fetch the current notice from the server whenever the app is in the foreground
    // and whenever the language or endpoint changes. The server response is the source
    // of truth: a null response withdraws a previously stored notice.
    useAsyncEffect(
        async isMounted => {
            if (state !== 'active' || appStatus !== 'active') {
                return;
            }

            // The app locale values match the LanguageCode enum values
            const language: LanguageCode = locale === 'FI' ? 'FI' : 'EN';

            try {
                const fetchedNotice = await fetchServiceNotice(createServiceNoticeClient(config), language);

                // isMounted() is false once this effect has been superseded by a newer run (a
                // foreground return, a language change). Stop here in that case, so a slow
                // answer can never overwrite what a newer, faster one already stored.
                if (!isMounted()) {
                    return;
                }

                if (fetchedNotice) {
                    await setPersistedNotice(fetchedNotice);
                } else {
                    await clearPersistedNotice();
                }

                // The server answered, so the service is alive: forget any earlier fallback
                // decision. Without this, one failed request after the fallback end date would
                // keep the app closed for good, even if the closing was postponed or cancelled.
                await clearServiceEndedByFallback();

                setNotice(fetchedNotice);
            } catch (error: unknown) {
                // The service is treated as closed only when the server could not be asked at
                // all AND the fallback end of service has passed: that is the case where the
                // backend has been switched off after the service ended (see
                // service-notice-fallback.ts). An answer from the service — including "no
                // notice" and a GraphQL error — never ends up here, so a notice that was
                // cancelled or postponed can not close the app by mistake.
                if (error instanceof ServiceNoticeUnreachableError && fallbackServiceEndHasPassed()) {
                    if (!isMounted()) {
                        return;
                    }

                    await setServiceEndedByFallback();
                    ErrorDiagnostics.closeErrorDiagnostics();
                    setState('unavailable');

                    return;
                }

                // Otherwise be forgiving: keep whatever notice state we already have
                ErrorDiagnostics.error(`Service notice check failed: ${error}`);
            }
        },
        [state, appStatus, locale, config]
    );

    // Watch the notice validity while a notice is active. The timer re-schedules itself
    // in short steps and re-checks immediately when the app returns to the foreground
    // (appStatus is a dependency), so the moment the validity passes is not missed.
    //
    // Strict consequence, intentional: once validUntil passes, the state flips to
    // 'unavailable' and the app makes no further server calls (the gate replaces the
    // whole app with a static screen and diagnostics are stopped). So if the notice is
    // withdrawn or its validUntil moved later after that moment — or its validUntil
    // already passed while the app was closed — the app cannot recover on its own; the
    // member has to reinstall or clear app storage. This is by design.
    useEffect(() => {
        if (state !== 'active' || !notice) {
            return;
        }

        let timer: ReturnType<typeof setTimeout> | undefined;

        const checkValidity = () => {
            const remaining = Date.parse(notice.validUntil) - Date.now();

            // A non-date validUntil would make `remaining` NaN; treat it as not-yet-passed
            // and stop, so we never reschedule with setTimeout(fn, NaN) in a tight loop.
            // (The persist guard already rejects such values; this is belt-and-suspenders.)
            if (Number.isNaN(remaining)) {
                return;
            }

            if (remaining <= 0) {
                // The notice validity has passed: stop error diagnostics and flag the
                // service as no longer available
                ErrorDiagnostics.closeErrorDiagnostics();
                setState('unavailable');
                return;
            }

            timer = setTimeout(checkValidity, Math.min(remaining, NOTICE_VALIDITY_CHECK_MAX_INTERVAL));
        };

        checkValidity();

        return () => clearTimeout(timer);
    }, [state, notice, appStatus]);

    const dismissNotice = useCallback(async () => {
        const now = new Date();
        await setNoticeDismissedAt(now);
        setDismissedAt(now);
    }, []);

    // The notice popup is shown while a notice is active, until dismissed; a dismissed
    // notice is shown again after the reminder interval so it is not forgotten
    const noticeVisible =
        state === 'active' &&
        !!notice &&
        (!dismissedAt || Date.now() - dismissedAt.getTime() >= NOTICE_REMINDER_INTERVAL);

    const value = useMemo(
        () => ({ state, notice, noticeVisible, dismissNotice }),
        [state, notice, noticeVisible, dismissNotice]
    );

    return <ServiceNoticeContext.Provider value={value}>{children}</ServiceNoticeContext.Provider>;
}

export const ServiceNoticeConsumer = ServiceNoticeContext.Consumer;
export const ServiceNoticeProvider = ServiceNoticeContextProvider;

export function useServiceNotice() {
    const context = useContext(ServiceNoticeContext);
    if (!context) {
        throw Error('Cannot use service notice context until it has been defined');
    }
    return context;
}
